<template>
  <div>
    <v-alert v-if="error" type="error" dismissible>
      {{ error }}
    </v-alert>

    <v-row>
      <v-col cols="auto" class="d-flex align-center mx-4">
        <span class="text-h5 font-weight-bold">公司打卡設定</span>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto" class="d-flex justify-end my-2">
        <v-btn
          color="primary"
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-capitalize
            btn-primary
            bg-gradient-primary
            py-3
            px-3
            mx-3
          "
          @click="openCreateDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          新增打卡設定
        </v-btn>
        <v-btn
          color="info"
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-capitalize
            btn-info
            bg-gradient-info
            py-3
            px-3
            mx-3
          "
          @click="copyDialog = true"
          :disabled="clockSettings.length === 0"
        >
          <v-icon left>mdi-content-copy</v-icon>
          複製打卡設定
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="clockSettings.length === 0 && !loading" class="text-center my-2">
      <v-icon size="64" color="grey lighten-1">mdi-clock-outline</v-icon>
      <h4 class="mt-3 text-h5 text-grey">尚無打卡設定</h4>
      <p class="text-body-1 text-grey">點擊上方按鈕新增打卡設定</p>
    </div>

    <div v-else>
      <company-clock-setting
        ref="clockSettingItems"
        v-for="setting in clockSettings"
        :key="setting.id"
        :clock-setting="setting"
        :only-one-setting="clockSettings.length <= 1"
        class="mb-4"
      ></company-clock-setting>
    </div>

    <!-- ProgressDialog for loading -->
    <progress-dialog :visible="loading" message="載入中，請稍候..." />

    <!-- Info Dialog -->
    <info-dialog
      :visible.sync="infoDialog.visible"
      :title="infoDialog.title"
      :message="infoDialog.message"
      :yes-button-text="infoDialog.yesButtonText"
      :yes-callback="infoDialog.yesCallback"
    />

    <!-- Copy Choice Dialog -->
    <v-dialog v-model="copyDialog" max-width="500px">
      <v-card class="border-radius-xl">
        <v-card-title class="text-h5 font-weight-bold px-6 pt-6">
          選擇要複製的打卡設定
        </v-card-title>
        <v-card-text class="px-6 pb-6">
          <v-list class="mt-4">
            <v-list-item
              v-for="setting in clockSettings"
              :key="setting.id || setting.name"
              @click="copyClockSetting(setting)"
              class="border-radius-md mb-2"
              color="primary"
            >
              <v-list-item-icon>
                <v-icon color="primary">mdi-content-copy</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{
                  setting.name
                }}</v-list-item-title>
                <v-list-item-subtitle v-if="setting.description">{{
                  setting.description
                }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            class="text-capitalize font-weight-bold"
            @click="copyDialog = false"
          >
            取消
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createDialog.visible" max-width="500px">
      <v-card class="border-radius-xl">
        <v-card-title class="text-h5 font-weight-bold px-6 pt-6">
          選擇打卡設定類型
        </v-card-title>
        <v-card-text class="px-6 pb-6">
          <v-list class="mt-4">
            <v-list-item
              @click="createNewSetting('fixed')"
              class="border-radius-md mb-2"
              color="primary"
            >
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-week</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  固定週休二日
                </v-list-item-title>
                <v-list-item-subtitle> 固定週休二日班表 </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              @click="createNewSetting('biweekly_flex')"
              class="border-radius-md mb-2"
              color="primary"
            >
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-range</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  雙週變形工時
                </v-list-item-title>
                <v-list-item-subtitle> 雙週變形工時班表 </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              @click="createNewSetting('fourweek_flex')"
              class="border-radius-md mb-2"
              color="primary"
            >
              <v-list-item-icon>
                <v-icon color="primary">mdi-calendar-multiselect</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  四週變形工時
                </v-list-item-title>
                <v-list-item-subtitle> 四週變形工時班表 </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            class="text-capitalize font-weight-bold"
            @click="closeCreateDialog"
          >
            取消
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CompanyClockSetting from "./CompanyClockSetting.vue";
import ProgressDialog from "@/components/Common/ProgressDialog.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import { SCHEDULE_TYPE } from "@/constants";

export default {
  name: "CompanyClockSettingList",
  components: {
    CompanyClockSetting,
    ProgressDialog,
    InfoDialog,
  },
  props: {
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      clockSettings: [],
      loading: false,
      error: null,
      deleteDialog: false,
      selectedSettingId: null,
      copyDialog: false,
      infoDialog: {
        visible: false,
        title: "操作成功",
        message: "",
        yesButtonText: "確定",
        yesCallback: null,
      },
      createDialog: {
        visible: false,
      },
    };
  },
  created() {
    this.fetchClockSettings();
  },
  watch: {
    companyId(newVal, oldVal) {
      if (!newVal || newVal === oldVal) return;
      this.fetchClockSettings();
    },
  },
  methods: {
    async fetchClockSettings() {
      this.loading = true;
      this.error = null;
      try {
        const response = await this.$axios.get(
          `/api/company-clock-settings/${this.companyId}`
        );
        this.clockSettings = response.data;
        console.log("Clock settings loaded:", this.clockSettings);
      } catch (err) {
        console.error("Error fetching clock settings:", err);
        this.error = "無法載入打卡設定資料";
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      console.log("openCreateDialog");
      this.createDialog.visible = true;
    },

    closeCreateDialog() {
      this.createDialog.visible = false;
    },

    buildClockSettingPayload({ fixed, scheduleType } = {}) {
      const isFixed = fixed !== undefined ? fixed : true;
      let effectiveScheduleType = scheduleType;

      if (!effectiveScheduleType) {
        effectiveScheduleType = isFixed
          ? SCHEDULE_TYPE.GENERAL
          : SCHEDULE_TYPE.BIWEEKLY_FLEX;
      }

      let name = "新打卡設定";
      let description = "請填寫說明 建議填寫 全工時/部份工時/排班等資訊";
      if (effectiveScheduleType === SCHEDULE_TYPE.BIWEEKLY_FLEX) {
        name = "雙週變形工時";
        description = "雙週變形工時打卡設定";
      } else if (effectiveScheduleType === SCHEDULE_TYPE.FOURWEEK_FLEX) {
        name = "四週變形工時";
        description = "四週變形工時打卡設定";
      }

      return {
        company_id: Number(this.companyId),
        name,
        description,
        fixed_working_days: isFixed,
        recess_day: "Saturday",
        regular_day: "Sunday",
        clock_in_allow_time: 5,
        clock_in_time: "08:00:00",
        clock_in_notify: 15,
        clock_out_allow_time: 5,
        clock_out_time: "17:00:00",
        clock_out_notify: 15,
        lunch_break: true,
        lunch_break_start: "12:30:00",
        lunch_break_end: "13:30:00",
        gps_enable: false,
        gps: "",
        gps_scope: 50,
        location: "",
        qr_fixed_enable: false,
        qr_fixed: "",
        qr_dynamic_enable: false,
        schedule_id: null,
        schedule_type: effectiveScheduleType,
        allow_shift_swap: true,
        extra: {},
        version: "v1",
      };
    },

    async createNewSetting(type) {
      this.closeCreateDialog();
      let options;

      if (type === "fixed") {
        options = { fixed: true, scheduleType: SCHEDULE_TYPE.GENERAL };
      } else if (type === "biweekly_flex") {
        options = { fixed: false, scheduleType: SCHEDULE_TYPE.BIWEEKLY_FLEX };
      } else if (type === "fourweek_flex") {
        options = { fixed: false, scheduleType: SCHEDULE_TYPE.FOURWEEK_FLEX };
      } else {
        options = { fixed: true, scheduleType: SCHEDULE_TYPE.GENERAL };
      }

      const payload = this.buildClockSettingPayload(options);
      this.addNewClockSetting(payload);
    },

    async addNewClockSetting(payload) {
      const data = payload || this.buildClockSettingPayload({ fixed: true });
      console.log("Add new clock setting:", payload);
      this.addClockSettingToList(payload);
    },

    copyClockSetting(setting) {
      this.copyDialog = false;
      // Create a copy of the setting, removing the id and changing the name
      const payload = JSON.parse(JSON.stringify(setting));
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;

      payload.name = "新打卡設定";
      payload.company_id = Number(this.companyId);

      console.log("Copy clock setting:", payload);
      this.addClockSettingToList(payload);
    },

    addClockSettingToList(payload) {
      this.clockSettings.push(payload);
      this.$nextTick(() => {
        const refs = this.$refs.clockSettingItems;
        const target = Array.isArray(refs) ? refs[refs.length - 1] : refs;
        if (target && typeof target.toggleEditMode === "function") {
          target.toggleEditMode();
          if (target.$el && typeof target.$el.scrollIntoView === "function") {
            target.$el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      });
    },

    confirmDeleteClockSetting(id) {
      if (this.clockSettings.length <= 1) {
        this.$emit("notify", "至少需保留一筆打卡設定，無法刪除最後一筆");
        return;
      }
      this.selectedSettingId = id;
      this.deleteDialog = true;
    },

    async deleteClockSetting() {
      this.loading = true;

      try {
        // This would call the API to delete the setting
        // await this.$axios.delete(`/api/company-clock-settings/${this.selectedSettingId}`);

        // For now, we'll just simulate deletion by removing from local array
        this.clockSettings = this.clockSettings.filter(
          (setting) => setting.id !== this.selectedSettingId
        );
        alert("打卡設定已刪除");
      } catch (err) {
        console.error("Error deleting clock setting:", err);
        alert("刪除打卡設定失敗");
      } finally {
        this.loading = false;
        this.deleteDialog = false;
        this.selectedSettingId = null;
      }
    },
  },
};
</script>
